/**
 * Hotspot Focal Slider — hotspot-focal-slider.js
 *
 * Custom stacked carousel:
 *  - Active card is largest (555×800) and on top
 *  - ±1 cards partially visible behind center
 *  - ±2 cards further behind, more faded
 *  - Smooth CSS transition on all property changes
 *  - Touch/swipe + click navigation + keyboard
 */

class HotspotFocalSlider {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.config = window.hfsConfig?.[this.sectionId] || {};
    this.slides = this.config.slides || [];
    this.total = this.slides.length;
    this.current = this.total > 0 ? Math.floor(this.total / 2) : 0; // start at center
    this.isAnimating = false;
    this.activeHotspotKey = null;

    // Touch state
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchDeltaX = 0;
    this.suppressClick = false;

    this.init();
  }

  /* ────────────────────────────────────────────
     Init
  ──────────────────────────────────────────── */
  init() {
    this.buildCards();
    this.renderOffsets();
    this.buildPagination();
    this.bindNav();
    this.bindTouch();
    this.bindHotspots();
    this.bindKeyboard();
    this.bindOutsideClick();
  }

  /* ── Build card DOM from config ── */
  buildCards() {
    const stage = this.section.querySelector(".hfs-stage");
    if (!stage) return;

    this.cards = [];

    this.slides.forEach((slide, i) => {
      const card = document.createElement("div");
      card.className = "hfs-card";
      card.dataset.index = i;

      /* Image */
      const imgHtml = slide.image
        ? `<img class="hfs-card__img" src="${slide.image}" alt="${slide.heading || ""}" loading="lazy">`
        : `<div class="hfs-card__placeholder">No image</div>`;

      /* Label */
      const labelHtml = slide.heading
        ? `<h5 class="hfs-card__label">${slide.heading}</h5>`
        : "";

      /* Link overlay — real anchor so browser shows the URL on hover */
      const linkHtml = slide.link
        ? `<a class="hfs-card__link" href="${slide.link}" aria-label="${slide.heading || "View"}"></a>`
        : "";

      /* Hotspots */
      const hotspotsHtml = [1, 2, 3]
        .map((n) => {
          const h = slide[`hotspot${n}`];
          if (!h || !h.enabled) return "";
          return `<div class="hfs-hotspot"
     data-slide="${i}" data-hotspot="${n}"
     data-x="${h.x}" data-y="${h.y}"
     data-xm="${h.xMobile ?? h.x}" data-ym="${h.yMobile ?? h.y}"
     style="left:${h.x}%;top:${h.y}%;">
<button class="hfs-hotspot__btn" type="button" aria-label="View product">
  <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60" fill="none" class="ripple-svg hfs-hotspot__icon">
    <circle class="hfs-hotspot__static-outer" opacity="0.2" cx="30" cy="30" r="30" fill="white"/>
    <circle class="hfs-hotspot__static-mid" opacity="0.2" cx="30" cy="30" r="20" fill="white"/>
    <circle class="hfs-hotspot__ripple" opacity="0" cx="30" cy="30" r="30" fill="white"/>
    <circle class="hfs-hotspot__static-inner" cx="30" cy="30" r="10" fill="white"/>
  </svg>
  
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-plus" viewBox="0 0 10 10" 
       style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 12px; height: 12px; color:#897A69;">
    <path fill="currentColor" fill-rule="evenodd" d="M1 4.51a.5.5 0 0 0 0 1h3.5l.01 3.5a.5.5 0 0 0 1-.01V5.5l3.5-.01a.5.5 0 0 0-.01-1H5.5L5.49.99a.5.5 0 0 0-1 .01v3.5l-3.5.01z" clip-rule="evenodd"/>
  </svg>
  </button>

</div>`;
        })
        .join("");

      card.innerHTML = imgHtml + linkHtml + labelHtml + hotspotsHtml;

      /* Click: non-active card → navigate to it, active card → anchor follows link */
      card.addEventListener("click", (e) => {
        if (this.suppressClick) {
          e.preventDefault();
          return;
        }
        if (e.target.closest(".hfs-hotspot")) return;
        const offset = this.getOffset(i);
        if (offset !== 0) {
          e.preventDefault();
          this.goTo(i);
        }
      });

      stage.appendChild(card);
      this.cards.push(card);
    });
  }

  /* ── Set data-offset on every card based on current active ── */
  renderOffsets() {
    this.cards.forEach((card, i) => {
      let offset = i - this.current;

      // Clamp far offsets to a named class
      if (offset <= -4 || offset >= 4) {
        card.dataset.offset = "far";
      } else {
        card.dataset.offset = String(offset);
      }
    });

    // Update pagination
    this.updatePagination();
    // Update hotspot positions for mobile/desktop
    this.repositionHotspots();
  }

  getOffset(index) {
    return index - this.current;
  }

  /* ── Navigate to a specific index ── */
  goTo(index, force = false) {
    if (this.isAnimating && !force) return;
    if (index < 0 || index >= this.total) return;
    if (index === this.current) return;

    this.isAnimating = true;
    this.closeCard();
    this.current = index;
    this.renderOffsets();

    setTimeout(() => {
      this.isAnimating = false;
    }, 680);
  }

  prev() {
    this.goTo(this.current - 1);
  }
  next() {
    this.goTo(this.current + 1);
  }

  /* ── Pagination ── */
  buildPagination() {
    const wrap = this.section.querySelector(".hfs-pagination");
    if (!wrap) return;

    this.dots = [];
    for (let i = 0; i < this.total; i++) {
      const btn = document.createElement("button");
      btn.className = "hfs-pagination__dot";
      btn.setAttribute("aria-label", `Go to slide ${i + 1}`);
      btn.addEventListener("click", () => this.goTo(i));
      wrap.appendChild(btn);
      this.dots.push(btn);
    }
    this.updatePagination();
  }

  updatePagination() {
    if (!this.dots) return;
    this.dots.forEach((d, i) => {
      d.classList.toggle("is-active", i === this.current);
    });
  }

  /* ── Navigation buttons ── */
  bindNav() {
    const prev = this.section.querySelector(".hfs-nav-btn--prev");
    const next = this.section.querySelector(".hfs-nav-btn--next");
    if (prev) prev.addEventListener("click", () => this.prev());
    if (next) next.addEventListener("click", () => this.next());
  }

  /* ── Touch / swipe ── */
  bindTouch() {
    const stage = this.section.querySelector(".hfs-stage");
    if (!stage) return;

    stage.addEventListener(
      "touchstart",
      (e) => {
        this.touchStartX = e.touches[0].clientX;
        this.touchStartY = e.touches[0].clientY;
        this.touchDeltaX = 0;
        this.suppressClick = false;
      },
      { passive: true },
    );

    stage.addEventListener(
      "touchmove",
      (e) => {
        this.touchDeltaX = e.touches[0].clientX - this.touchStartX;
        if (Math.abs(this.touchDeltaX) > 10) this.suppressClick = true;
        const deltaY = e.touches[0].clientY - this.touchStartY;
        // Prevent page scroll only if horizontal swipe
        if (Math.abs(this.touchDeltaX) > Math.abs(deltaY)) {
          e.preventDefault();
        }
      },
      { passive: false },
    );

    stage.addEventListener("touchend", () => {
      const threshold = 50;
      if (this.touchDeltaX < -threshold) this.next();
      else if (this.touchDeltaX > threshold) this.prev();
      this.touchDeltaX = 0;
    });

    /* Mouse drag */
    let mouseDown = false;
    let mouseStartX = 0;

    stage.addEventListener("mousedown", (e) => {
      mouseDown = true;
      mouseStartX = e.clientX;
      this.suppressClick = false;
    });
    window.addEventListener("mousemove", (e) => {
      if (!mouseDown) return;
      this.touchDeltaX = e.clientX - mouseStartX;
      if (Math.abs(this.touchDeltaX) > 10) this.suppressClick = true;
    });
    window.addEventListener("mouseup", () => {
      if (!mouseDown) return;
      mouseDown = false;
      const threshold = 60;
      if (this.touchDeltaX < -threshold) this.next();
      else if (this.touchDeltaX > threshold) this.prev();
      this.touchDeltaX = 0;
    });
  }

  /* ── Keyboard ── */
  bindKeyboard() {
    document.addEventListener("keydown", (e) => {
      if (!this.section.matches(":hover, :focus-within")) return;
      if (e.key === "ArrowLeft") this.prev();
      if (e.key === "ArrowRight") this.next();
    });
  }

  /* ── Hotspot interaction ── */
  bindHotspots() {
    this.section.addEventListener("click", (e) => {
      const btn = e.target.closest(".hfs-hotspot__btn");
      if (!btn) return;

      const dot = btn.closest(".hfs-hotspot");
      const si = parseInt(dot.dataset.slide, 10);
      const hn = parseInt(dot.dataset.hotspot, 10);
      const key = `${si}-${hn}`;

      // Only fire for the active card's hotspots
      if (si !== this.current) return;

      if (this.activeHotspotKey === key) {
        this.closeCard();
      } else {
        this.openCard(si, hn, dot, key);
      }
    });

    window.addEventListener("resize", () => this.repositionHotspots());
  }

  bindOutsideClick() {
    document.addEventListener("click", (e) => {
      const card = this.section.querySelector(".hfs-product-card");
      if (!card || !card.classList.contains("is-visible")) return;

      const isInsideCard = card.contains(e.target);
      const isHotspot = e.target.closest(".hfs-hotspot__btn");

      if (!isInsideCard && !isHotspot) {
        this.closeCard();
      }
    });
  }

  repositionHotspots() {
    const mobile = window.matchMedia("(max-width:749px)").matches;
    this.section.querySelectorAll(".hfs-hotspot").forEach((dot) => {
      dot.style.left = `${mobile ? dot.dataset.xm : dot.dataset.x}%`;
      dot.style.top = `${mobile ? dot.dataset.ym : dot.dataset.y}%`;
    });
  }

  openCard(si, hn, dot, key) {
    this.closeCard();
    this.activeHotspotKey = key;
    dot.classList.add("is-open");

    const slide = this.slides[si];
    const hotspot = slide?.[`hotspot${hn}`];
    if (!hotspot?.productHandle) return;

    const card = this.section.querySelector(".hfs-product-card");
    if (!card) return;

    card.classList.add("is-loading");
    card.querySelector(".hfs-product-card__img-wrap").innerHTML =
      `<div class="hfs-product-card__img-placeholder"></div>`;
    card.querySelector(".hfs-product-card__title").textContent = "";
    card.querySelector(".hfs-product-card__prices").innerHTML = "";
    card.classList.add("is-visible");

    fetch(`/products/${hotspot.productHandle}.js`)
      .then((r) => r.json())
      .then((p) => {
        if (this.activeHotspotKey === key) {
          this.renderProduct(p, card);
        }
      })
      .catch(() => {
        card.classList.remove("is-visible");
        card.classList.remove("is-loading");
      });
  }

  renderProduct(product, card) {
    card.classList.remove("is-loading");
    const variant = product.variants?.[0] || {};
    const imgSrc = product.featured_image || product.images?.[0] || "";
    const title = product.title || "";
    const price = this.money(variant.price ?? product.price);
    const compare = variant.compare_at_price
      ? this.money(variant.compare_at_price)
      : null;

    card.querySelector(".hfs-product-card__img-wrap").innerHTML = imgSrc
      ? `<img src="${imgSrc}" alt="${title}" loading="lazy">`
      : `<div class="hfs-product-card__img-placeholder">No image</div>`;

    card.querySelector(".hfs-product-card__title").textContent = title;
    card.querySelector(".hfs-product-card__prices").innerHTML =
      `<span class="hfs-product-card__price">${price}</span>` +
      (compare
        ? `<span class="hfs-product-card__compare">${compare}</span>`
        : "");

    card.classList.add("is-visible");
  }

  closeCard() {
    this.activeHotspotKey = null;
    this.section
      .querySelectorAll(".hfs-hotspot.is-open")
      .forEach((d) => d.classList.remove("is-open"));
    const card = this.section.querySelector(".hfs-product-card");
    if (card) {
      card.classList.remove("is-visible");
      card.classList.remove("is-loading");
    }
  }

  money(cents) {
    if (cents == null) return "";
    const sym = window.Shopify?.currency?.active || "";
    return `${sym} ${(cents / 100).toFixed(2)}`.trim();
  }
}

/* ── Bootstrap ── */
function initHotspotFocalSliders() {
  document.querySelectorAll("[data-hotspot-focal-slider]").forEach((s) => {
    if (!s.dataset.initialized) {
      new HotspotFocalSlider(s);
      s.dataset.initialized = "true";
    }
  });
}

document.addEventListener("DOMContentLoaded", initHotspotFocalSliders);

document.addEventListener("shopify:section:load", (e) => {
  const s = e.target.querySelector("[data-hotspot-focal-slider]") || e.target;
  if (s.hasAttribute("data-hotspot-focal-slider") && !s.dataset.initialized) {
    new HotspotFocalSlider(s);
    s.dataset.initialized = "true";
  }
});
